import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, GridItem, GridRow, Icon, MenuItem, ScrollableDrawer, Select, Spacer, Stack, TextField, Typography } from '@emburse/embark-core';
import { ArrowBackIcon, ArrowForwardIcon } from '@emburse/embark-icons';
import { isEqual } from 'lodash';
import { Checkbox, CurrencySelect } from '../../../../../components';
import { getDepartments } from '../../../../../api/api';
import { Employee } from '../../../../../interfaces/Employee';
import { validateEmail } from '../../../../../util/formValidation';
import { $t, useTranslation } from '../../../../../hooks/useTranslation';
function UsersDrawer({ currentRow, showDrawer, onSetShowDrawer, onSave, onDelete, onPrevious, onNext, empTypes, currencies }) {
    useTranslation();
    const [firstName, setFirstName] = useState({
        value: currentRow?.FirstName,
        validationText: ''
    });
    const [lastName, setLastName] = useState({ value: currentRow?.LastName, validationText: '' });
    const [email, setEmail] = useState({ value: currentRow?.Email, validationText: '' });
    const [empID, setEmpID] = useState({ value: currentRow?.EmpID, validationText: '' });
    const [empType, setEmpType] = useState({ value: currentRow?.EmpType, validationText: '' });
    const [deptName, setDeptName] = useState({ value: currentRow?.DeptName, validationText: '' });
    const [approverEmail, setApproverEmail] = useState({
        value: currentRow?.ApproverEmail,
        validationText: ''
    });
    const [accountantEmail, setAccountantEmail] = useState({
        value: currentRow?.AccountantEmail,
        validationText: ''
    });
    const [currencyType, setCurrencyType] = useState({
        value: currentRow?.CurrencyType,
        validationText: ''
    });
    const [multipleCurrencies, setMultipleCurrencies] = useState({
        value: currentRow?.MultipleCurrencies,
        validationText: ''
    });
    const [useVAT, setUseVAT] = useState({ value: currentRow?.UseVAT, validationText: '' });
    const [defaultVATRate, setDefaultVATRate] = useState({
        value: currentRow?.DefaultVATRate,
        validationText: ''
    });
    const [usePST, setUsePST] = useState({ value: currentRow?.UsePST, validationText: '' });
    const [defaultPSTRate, setDefaultPSTRate] = useState({
        value: currentRow?.DefaultPSTRate,
        validationText: ''
    });
    const [useHST, setUseHST] = useState({ value: currentRow?.UseHST, validationText: '' });
    const [defaultHSTRate, setDefaultHSTRate] = useState({
        value: currentRow?.DefaultHSTRate,
        validationText: ''
    });
    const [departments, setDepartments] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    const defaultStateValues = useCallback((useCurrentRow = false) => {
        setFirstName({ value: useCurrentRow ? currentRow?.FirstName : '', validationText: '' });
        setLastName({ value: useCurrentRow ? currentRow?.LastName : '', validationText: '' });
        setEmail({ value: useCurrentRow ? currentRow?.Email : '', validationText: '' });
        setEmpID({ value: useCurrentRow ? currentRow?.EmpID : '', validationText: '' });
        setEmpType({ value: useCurrentRow ? currentRow?.EmpType : '', validationText: '' });
        setDeptName({ value: useCurrentRow ? currentRow?.DeptName : '', validationText: '' });
        setApproverEmail({
            value: useCurrentRow ? currentRow?.ApproverEmail : '',
            validationText: ''
        });
        setAccountantEmail({
            value: useCurrentRow ? currentRow?.AccountantEmail : '',
            validationText: ''
        });
        setCurrencyType({
            value: useCurrentRow ? currentRow?.CurrencyType : '',
            validationText: ''
        });
        setMultipleCurrencies({
            value: useCurrentRow ? currentRow?.MultipleCurrencies : false,
            validationText: ''
        });
        setUseVAT({ value: useCurrentRow ? currentRow?.UseVAT : false, validationText: '' });
        setDefaultVATRate({
            value: useCurrentRow ? currentRow?.DefaultVATRate : null,
            validationText: ''
        });
        setUsePST({ value: useCurrentRow ? currentRow?.UsePST : false, validationText: '' });
        setDefaultPSTRate({
            value: useCurrentRow ? currentRow?.DefaultPSTRate : null,
            validationText: ''
        });
        setUseHST({ value: useCurrentRow ? currentRow?.UseHST : false, validationText: '' });
        setDefaultHSTRate({
            value: useCurrentRow ? currentRow?.DefaultHSTRate : null,
            validationText: ''
        });
    }, [currentRow]);
    useEffect(() => {
        defaultStateValues(showDrawer);
    }, [defaultStateValues, showDrawer]);
    useEffect(() => {
        if (!departments.length) {
            getDepartments({ Emp: new Employee() }).then(setDepartments);
        }
    }, [departments]);
    const fieldChangeHandler = (e, field) => {
        const fieldMap = {
            FirstName: setFirstName,
            LastName: setLastName,
            Email: setEmail,
            EmpID: setEmpID,
            EmpType: setEmpType,
            DeptName: setDeptName,
            ApproverEmail: setApproverEmail,
            AccountantEmail: setAccountantEmail,
            MultipleCurrencies: setMultipleCurrencies,
            UseVAT: setUseVAT,
            DefaultVATRate: setDefaultVATRate,
            UsePST: setUsePST,
            DefaultPSTRate: setDefaultPSTRate,
            UseHST: setUseHST,
            DefaultHSTRate: setDefaultHSTRate
        };
        if (field.includes('Use') || field.includes('Multiple')) {
            fieldMap[field]((prevState) => ({ ...prevState, value: e.target.checked }));
        }
        else {
            fieldMap[field]((prevState) => ({
                ...prevState,
                value: e.target.value,
                validationText: ''
            }));
        }
    };
    const checkValidation = () => {
        let valid = true;
        const required = [
            [firstName.value, setFirstName],
            [lastName.value, setLastName],
            [email.value, setEmail],
            [deptName.value, setDeptName]
        ];
        const newValidationResults = [];
        if (validateEmail(email.value).includes('valid')) {
            setEmail((prevState) => ({ ...prevState, validationText: validateEmail(email.value) }));
            newValidationResults.push($t('Email address is invalid'));
            valid = false;
        }
        required.forEach(([value, setter]) => {
            if (!value || value.length === 0) {
                setter((prevState) => ({
                    ...prevState,
                    validationText: $t('This field is required')
                }));
                if (!newValidationResults.includes($t('Some required fields are empty.'))) {
                    newValidationResults.push($t('Some required fields are empty.'));
                }
                valid = false;
            }
        });
        setValidationResults(newValidationResults);
        return valid;
    };
    const saveHandler = () => {
        const isValid = checkValidation();
        if (isValid) {
            setValidationResults([]);
            const currentData = JSON.parse(JSON.stringify(currentRow));
            currentData.FirstName = firstName.value;
            currentData.LastName = lastName.value;
            currentData.Email = email.value;
            currentData.EmpID = empID.value;
            currentData.EmpType = empType.value;
            currentData.DeptName = deptName.value;
            currentData.DeptCode = departments.find((dept) => dept.Name === deptName.value).PK;
            currentData.ApproverEmail = approverEmail.value;
            currentData.AccountantEmail = accountantEmail.value;
            currentData.CurrencyType = currencyType.value;
            currentData.MultipleCurrencies = multipleCurrencies.value;
            currentData.UseVAT = useVAT.value;
            currentData.DefaultVATRate = defaultVATRate.value;
            currentData.UsePST = usePST.value;
            currentData.DefaultPSTRate = defaultPSTRate.value;
            currentData.UseHST = useHST.value;
            currentData.DefaultHSTRate = defaultHSTRate.value;
            onSave(currentData);
        }
    };
    const hasChanges = () => {
        const currentState = {
            id: currentRow?.id,
            FirstName: firstName.value,
            LastName: lastName.value,
            Email: email.value,
            EmpID: empID.value,
            EmpType: empType.value,
            DeptName: deptName.value,
            ApproverEmail: approverEmail.value,
            AccountantEmail: accountantEmail.value,
            CurrencyType: currencyType.value,
            MultipleCurrencies: multipleCurrencies.value,
            UseVAT: useVAT.value,
            DefaultVATRate: defaultVATRate.value,
            UsePST: usePST.value,
            DefaultPSTRate: defaultPSTRate.value,
            UseHST: useHST.value,
            DefaultHSTRate: defaultHSTRate.value
        };
        let parentData = currentRow;
        if (currentRow) {
            parentData = JSON.parse(JSON.stringify(currentRow));
            if (parentData?.PK) {
                delete parentData.PK;
            }
            delete parentData.DeptCode;
        }
        return !isEqual(parentData, currentState);
    };
    return (_jsxs(ScrollableDrawer, { open: showDrawer, anchor: "right", sideWidth: 448, openUpdated: onSetShowDrawer, header: $t('User Details'), footer: _jsxs(GridRow, { justifyContent: "space-between", children: [_jsx(GridItem, { children: hasChanges() ? (_jsx(Button, { color: "primary", variant: "outlined", onClick: () => onSetShowDrawer(false), children: $t('Cancel') })) : (_jsx(Button, { variant: "text", startIcon: _jsx(Icon, { iconUrl: ArrowBackIcon }), onClick: onPrevious, size: "small", children: $t('Previous') })) }), _jsx(GridItem, { children: hasChanges() ? (_jsx(Button, { color: "primary", onClick: saveHandler, children: $t('Save Changes') })) : (_jsx(Button, { variant: "text", endIcon: _jsx(Icon, { iconUrl: ArrowForwardIcon }), onClick: onNext, size: "small", children: $t('Next') })) })] }), children: [_jsxs(Stack, { spacing: 3, direction: "column", alignItems: "stretch", justifyContent: "space-between", children: [validationResults.length > 0 && (_jsx(Alert, { severity: "error", alertTitle: $t('Please fix the following:'), children: validationResults.map((item) => (_jsx(Typography, { children: item }, `${item.Key}`))) })), _jsx(Typography, { variant: "subtitle1", children: "General Settings" }), _jsx(TextField, { id: "FirstName", name: "First Name", fullWidth: true, label: $t('First Name'), type: "text", value: firstName.value, helperText: firstName.validationText, error: !!firstName.validationText, onChange: (e) => fieldChangeHandler(e, 'FirstName') }), _jsx(TextField, { id: "LastName", name: "Last Name", fullWidth: true, label: $t('Last Name'), type: "text", value: lastName.value, helperText: lastName.validationText, error: !!lastName.validationText, onChange: (e) => fieldChangeHandler(e, 'LastName') }), _jsx(TextField, { id: "EmailAddress", name: "Email Address", fullWidth: true, label: $t('Email Address'), type: "text", value: email.value, helperText: email.validationText, error: !!email.validationText, onChange: (e) => fieldChangeHandler(e, 'Email') }), _jsx(TextField, { id: "EmployeeID", name: "Employee ID", fullWidth: true, label: $t('Employee ID (if applicable)'), type: "text", value: empID.value, helperText: empID.validationText, error: !!empID.validationText, onChange: (e) => fieldChangeHandler(e, 'EmpID') }), _jsx(Select, { id: "EmployeeRole", style: { width: '100%', minWidth: 'auto' }, label: $t('Certify Role'), value: empType.value, helperText: empType.validationText, error: !!empType.validationText, onChange: (e) => fieldChangeHandler(e, 'EmpType'), children: empTypes.map((item) => (_jsx(MenuItem, { value: item.name, children: item.name }, item.value))) }), _jsx(Select, { id: "DepartmentName", style: { width: '100%', minWidth: 'auto' }, label: $t('Department Name'), value: deptName.value, helperText: deptName.validationText, error: !!deptName.validationText, onChange: (e) => fieldChangeHandler(e, 'DeptName'), children: departments.map((item) => (_jsx(MenuItem, { value: item.Name, children: item.Name }, item.PK))) }), _jsx(TextField, { id: "ApproverEmailAddress", name: "Certify Approver Email Address", fullWidth: true, label: $t('Certify Approver Email Address'), type: "text", value: approverEmail.value, helperText: approverEmail.validationText, error: !!approverEmail.validationText, onChange: (e) => fieldChangeHandler(e, 'ApproverEmail') }), _jsx(TextField, { id: "AccountantEmailAddress", name: "Certify Accountant Email Address", fullWidth: true, label: $t('Certify Accountant Email Address'), type: "text", value: accountantEmail.value, helperText: accountantEmail.validationText, error: !!accountantEmail.validationText, onChange: (e) => fieldChangeHandler(e, 'AccountantEmail') }), _jsx(CurrencySelect, { id: "CurrencyType", style: { width: '100%', minWidth: 'auto' }, label: $t('Currency Type'), value: currencyType.value, options: currencies, helperText: currencyType.validationText, error: !!currencyType.validationText, onSelect: (currency, reason) => {
                            if (reason === 'clear') {
                                setCurrencyType((prevState) => ({
                                    ...prevState,
                                    value: 'USD',
                                    validationText: ''
                                }));
                            }
                            else {
                                setCurrencyType((prevState) => ({
                                    ...prevState,
                                    value: currency.Suffix,
                                    validationText: ''
                                }));
                            }
                        } }), _jsx(Checkbox, { id: "UseMultipleCurrencies", color: "primary", label: $t('Use Multiple Currencies'), checked: multipleCurrencies.value, onChange: (e) => fieldChangeHandler(e, 'MultipleCurrencies') }), _jsx(Checkbox, { id: "UseVAT", color: "primary", label: $t('Use VAT'), checked: useVAT.value, onChange: (e) => fieldChangeHandler(e, 'UseVAT') }), !!useVAT.value && (_jsx(TextField, { id: "DefaultVATRate", name: "Default VAT Rate", fullWidth: true, label: $t('Default VAT Rate'), type: "number", value: defaultVATRate.value, helperText: defaultVATRate.validationText, error: !!defaultVATRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultVATRate') })), _jsx(Checkbox, { id: "UsePST", color: "primary", label: $t('Use PST'), checked: usePST.value, onChange: (e) => fieldChangeHandler(e, 'UsePST') }), !!usePST.value && (_jsx(TextField, { id: "DefaultPSTRate", name: "Default PST Rate", fullWidth: true, label: $t('Default PST Rate'), type: "number", value: defaultPSTRate.value, helperText: defaultPSTRate.validationText, error: !!defaultPSTRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultPSTRate') })), _jsx(Checkbox, { id: "UseHST", color: "primary", label: $t('Use HST'), checked: useHST.value, onChange: (e) => fieldChangeHandler(e, 'UseHST') }), !!useHST.value && (_jsx(TextField, { id: "DefaultHSTRate", name: "Default HST Rate", fullWidth: true, label: $t('Default HST Rate'), type: "number", value: defaultHSTRate.value, helperText: defaultHSTRate.validationText, error: !!defaultHSTRate.validationText, onChange: (e) => fieldChangeHandler(e, 'DefaultHSTRate') }))] }), _jsxs(Stack, { spacing: 3, direction: "column", alignItems: "flex-start", children: [_jsx(Spacer, { size: 0 }), _jsx(Button, { color: "error", onClick: onDelete, variant: "outlined", children: $t('Deactivate User') })] })] }));
}
export default UsersDrawer;
